import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const nombreDe = (persona) => (
    [persona?.nombre, persona?.primer_apellido, persona?.segundo_apellido]
        .filter(Boolean).join(' ')
);

/**
 * Recuadro con lo que ha entendido el lector del libro de familia: a quién ha
 * colocado en cada sitio, qué no ha sabido clasificar y qué páginas han fallado.
 *
 * Es lo que le dice al agente dónde tiene que mirar antes de generar el PDF.
 */
const ResumenLectura = ({ lectura, lecturaDni, onVolverASubir }) => {
    const { t } = useTranslation();
    if (!lectura && !lecturaDni) return null;

    const hijos = lectura?.hijos || [];
    const solicitante = lectura?.solicitante || {};
    const otro = lectura?.otro_progenitor || {};
    const sinClasificar = lectura?.sin_clasificar || [];
    const errores = lectura?.errores || [];
    // Incoherencias que el lector ha detectado solo: fechas imposibles, el padre
    // sin identificar... No impiden generar el PDF, pero hay que mirarlas.
    const avisos = lectura?.avisos || [];

    const leidos = [];
    // El DNI manda sobre el libro de familia para identificar al solicitante.
    if (lecturaDni?.encontrado) {
        leidos.push({
            etiqueta: t('Solicitante') + ' (DNI)',
            texto: [nombreDe(lecturaDni), lecturaDni.dni_nie].filter(Boolean).join(' · '),
        });
        if (lecturaDni.direccion) {
            leidos.push({ etiqueta: t('Domicilio'), texto: [lecturaDni.direccion, lecturaDni.codigo_postal, lecturaDni.provincia].filter(Boolean).join(', ') });
        }
    } else if (nombreDe(solicitante)) {
        leidos.push({ etiqueta: t('Solicitante'), texto: nombreDe(solicitante) });
    }
    if (nombreDe(otro)) leidos.push({ etiqueta: t('Otro progenitor'), texto: nombreDe(otro) });
    hijos.forEach((hijo) => leidos.push({
        etiqueta: t('Hijo'),
        texto: [nombreDe(hijo), hijo.fecha_nacimiento].filter(Boolean).join(' · '),
    }));

    const hayProblemas = sinClasificar.length > 0 || errores.length > 0;

    return (
        <div className="mb-8 rounded-lg border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-900/20 p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-100">
                    <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                    {t('Lo que se ha leído de la documentación')}
                </h3>
                {onVolverASubir && (
                    <button
                        type="button"
                        onClick={onVolverASubir}
                        className="flex-shrink-0 text-sm text-primary-600 hover:underline dark:text-primary-400"
                    >
                        {t('Volver a subir')}
                    </button>
                )}
            </div>

            {leidos.length === 0 && (
                <p className="text-sm text-red-700 dark:text-red-300">
                    {t('No se ha podido extraer nada de las imágenes.')}
                </p>
            )}

            {leidos.length > 0 && (
                <ul className="space-y-1 text-sm text-blue-900 dark:text-blue-100">
                    {leidos.map((item, i) => (
                        <li key={i}>
                            <span className="font-semibold">{item.etiqueta}:</span> {item.texto}
                        </li>
                    ))}
                </ul>
            )}

            {avisos.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm text-amber-700 dark:text-amber-300">
                    {avisos.map((aviso, i) => (
                        <li key={`av-${i}`} className="flex gap-2">
                            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>{aviso}</span>
                        </li>
                    ))}
                </ul>
            )}

            {hayProblemas && (
                <ul className="mt-3 space-y-1 text-sm text-red-700 dark:text-red-300">
                    {sinClasificar.map((persona, i) => (
                        <li key={`sc-${i}`} className="flex gap-2">
                            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                {t('Sin clasificar')}: {nombreDe(persona) || persona.apellidos || t('(sin nombre)')} — {t('colócalo tú a mano')}
                            </span>
                        </li>
                    ))}
                    {errores.map((error, i) => (
                        <li key={`err-${i}`} className="flex gap-2">
                            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                {t('No se pudo leer')} <code className="font-mono">{error.archivo || '?'}</code>: {error.motivo}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <p className="mt-3 text-xs text-blue-800 dark:text-blue-200">
                {t('Revísalo y corrige lo que haga falta.')} <strong>{t('Los DNI de los hijos no vienen en ningún documento')}</strong>, {t('hay que teclearlos.')}
            </p>
        </div>
    );
};

ResumenLectura.propTypes = {
    lectura: propTypes.object,
    lecturaDni: propTypes.object,
    onVolverASubir: propTypes.func,
};

export default ResumenLectura;
